// 폼 값 → CreateAlimtalkSendDto 변환 + 클라이언트 검증 (순수 함수). 백엔드 §6.3.1 조합 검증 미러.

import dayjs from '../../lib/dayjs';
import { formatDate } from '../../lib/utils/date/formatDate';
import { AlimtalkFormCouponItem, AlimtalkSendFormValues, FieldRequirements } from '../../types/alimtalk/form';
import { CreateAlimtalkSendDto, TestAlimtalkSendDto } from '../../types/alimtalk/send';
import { ALIMTALK_EXPIRE_ISSUED_COUNT } from '../../constants/service/app/alimtalkConstants';
import { COUPON_PERIOD_TYPE } from '../../constants/service/app/couponConstants';

export const ALIMTALK_VALIDATION_MSG = {
  TEMPLATE_REQUIRED: '발송 유형을 선택해주세요.',
  CSV_REQUIRED: 'CSV 파일을 업로드해주세요.',
  MEMO_REQUIRED: '메모를 입력해주세요.',
  MEMO_MAX_LENGTH: '메모는 500자 이하로 입력해주세요.',
  COUPON_REQUIRED: '쿠폰을 선택해주세요.',
  ISSUED_COUNT_MIN: '발급 수량은 1장 이상이어야 합니다.',
  VALIDITY_REQUIRED: '쿠폰 유효기간을 입력해주세요.',
  VALIDITY_ORDER: '쿠폰 종료일은 시작일보다 이후여야 합니다.',
  ALIMTALK_COUPON_REQUIRED: '알림톡에 표시할 쿠폰을 선택해주세요.',
  ALIMTALK_COUPON_IN_LIST: '알림톡 쿠폰은 선택한 쿠폰 중에서 골라야 합니다.',
  EVENT_REQUIRED: '기획전을 선택해주세요.',
  SCHEDULED_PAST: '예약 시간은 현재 시각 이후로 설정해주세요.',
  TEST_PHONE_INVALID: '전화번호는 10~11자리 숫자로 입력해주세요.',
} as const;

/** 쿠폰 기간 표기 — 유효기간 힌트·검증 에러가 같은 포맷을 쓰도록 공유. 비정상 데이터(종료일 없음)도 드러나게 표기 */
export const formatCouponPeriod = (start: string | null, end: string | null): string => {
  if (!end) return start ? `${formatDate(start)} ~ (종료일 없음)` : '(기간 정보 없음)';
  return start ? `${formatDate(start)} ~ ${formatDate(end)}` : `${formatDate(end)}까지`;
};

/**
 * 발급 유효기간 검증 — '날짜 지정' 쿠폰들의 기간 제약을 집합으로 모아 판정한다.
 * (쌍별 비교는 기준 쿠폰의 null startDate에 가려 뒤쪽 쿠폰 간 충돌을 놓칠 수 있어 제약 집합 방식 사용)
 * - 각 쿠폰의 endDate는 입력 종료일과 초 단위 일치 필수
 * - startDate를 가진 쿠폰은 입력 시작일과 초 단위 일치 필수 (startDate 없는 쿠폰은 종료일만 요구 — 워커도 endDate만 사용)
 * 반환: 에러 메시지 또는 null
 */
const validateCouponPeriods = (
  couponList: AlimtalkFormCouponItem[],
  startDate: string,
  endDate: string,
): string | null => {
  const fixedDateCoupons = couponList.filter(
    (coupon) => coupon.periodType === COUPON_PERIOD_TYPE.START_AND_END_DATE,
  );
  if (fixedDateCoupons.length === 0) return null;

  // 비정상 데이터(날짜 지정인데 종료일 없음)는 조용히 통과시키지 않고 명시 차단 — 쿠폰 설정 문제를 조기 노출
  const broken = fixedDateCoupons.find((coupon) => !coupon.endDate);
  if (broken) {
    return `쿠폰 '${broken.name}'의 유효기간 정보가 올바르지 않습니다(${formatCouponPeriod(broken.startDate, broken.endDate)}). 쿠폰 설정을 확인해주세요.`;
  }

  const epochOf = (value: string) => dayjs(value).startOf('second').valueOf();

  // 쿠폰들 간 요구 기간이 서로 다르면 하나의 발급 기간으로 만족 불가 — 대표 쌍으로 안내
  const differentEnd = fixedDateCoupons.find(
    (coupon) => epochOf(coupon.endDate as string) !== epochOf(fixedDateCoupons[0].endDate as string),
  );
  const startRequirers = fixedDateCoupons.filter((coupon) => coupon.startDate != null);
  const differentStart = startRequirers.find(
    (coupon) => epochOf(coupon.startDate as string) !== epochOf(startRequirers[0]?.startDate as string),
  );
  if (differentEnd || differentStart) {
    const base = differentEnd ? fixedDateCoupons[0] : startRequirers[0];
    const other = (differentEnd ?? differentStart) as AlimtalkFormCouponItem;
    return `기간이 서로 다른 날짜 지정 쿠폰('${base.name}', '${other.name}')은 함께 발급할 수 없습니다. 발송을 나눠 등록해주세요.`;
  }

  // 여기 도달 시 요구 기간은 유일 — 입력과 대조
  const endMismatch = epochOf(endDate) !== epochOf(fixedDateCoupons[0].endDate as string);
  const startMismatch =
    startRequirers.length > 0 && epochOf(startDate) !== epochOf(startRequirers[0].startDate as string);
  if (endMismatch || startMismatch) {
    const target = startMismatch ? startRequirers[0] : fixedDateCoupons[0];
    return `쿠폰 '${target.name}'의 유효기간(${formatCouponPeriod(target.startDate, target.endDate)})과 정확히 일치해야 합니다.`;
  }

  return null;
};

/** 백엔드 testPhone 패턴(/^\d{10,11}$/) 미러 — 하이픈 등 비숫자 제거 */
export const normalizeTestPhone = (phone: string): string => phone.replace(/\D/g, '');

export const isValidTestPhone = (phone: string): boolean => /^\d{10,11}$/.test(normalizeTestPhone(phone));

/** 발송 등록 전 검증. 통과 시 null, 실패 시 에러 메시지 반환. */
export const validateAlimtalkSendForm = (
  values: AlimtalkSendFormValues,
  requirements: FieldRequirements | null,
): string | null => {
  if (!values.templateId || !requirements) return ALIMTALK_VALIDATION_MSG.TEMPLATE_REQUIRED;
  // EXPIRE는 쿠폰 보유자 전원 자동 대상이라 CSV 없음 (#4841)
  if (requirements.needsCsvUpload && !values.csvS3Key) return ALIMTALK_VALIDATION_MSG.CSV_REQUIRED;

  // 백엔드 @IsNotEmpty @MaxLength(500) 미러
  if (!values.memo.trim()) return ALIMTALK_VALIDATION_MSG.MEMO_REQUIRED;
  if (values.memo.length > 500) return ALIMTALK_VALIDATION_MSG.MEMO_MAX_LENGTH;

  if (requirements.needsCouponList && values.couponList.length === 0) {
    return ALIMTALK_VALIDATION_MSG.COUPON_REQUIRED;
  }

  // 백엔드 @Min(1)은 만료 포함 전 유형 강제 (InputNumber min은 blur 시점에만 강제됨)
  if (requirements.needsCouponList && values.couponList.some((coupon) => coupon.issuedCount < 1)) {
    return ALIMTALK_VALIDATION_MSG.ISSUED_COUNT_MIN;
  }

  if (requirements.needsValidityDates) {
    if (!values.couponStartDate || !values.couponEndDate) return ALIMTALK_VALIDATION_MSG.VALIDITY_REQUIRED;
    if (!dayjs(values.couponEndDate).isAfter(dayjs(values.couponStartDate))) {
      return ALIMTALK_VALIDATION_MSG.VALIDITY_ORDER;
    }
    // 발급 유효기간은 쿠폰 자체 기간과 시각(초)까지 정확히 일치해야 함 — 운영 정책: 발급 기간 = 쿠폰 기간 그대로.
    // 비교 대상은 '날짜 지정' 쿠폰만 (발급일 기준·무기한은 비교할 절대 날짜가 없음)
    const periodError = validateCouponPeriods(values.couponList, values.couponStartDate, values.couponEndDate);
    if (periodError) return periodError;
  }

  if (requirements.needsAlimtalkCouponId) {
    if (!values.alimtalkCouponId) return ALIMTALK_VALIDATION_MSG.ALIMTALK_COUPON_REQUIRED;
    if (!values.couponList.some((coupon) => coupon.couponId === values.alimtalkCouponId)) {
      return ALIMTALK_VALIDATION_MSG.ALIMTALK_COUPON_IN_LIST;
    }
  }

  if (requirements.needsEventId && !values.eventId) return ALIMTALK_VALIDATION_MSG.EVENT_REQUIRED;

  // 예약(즉시 아님)인데 과거 시각이면 거부 (scheduledAt=null은 즉시 발송이므로 통과)
  if (values.scheduledAt && dayjs(values.scheduledAt).isBefore(dayjs())) {
    return ALIMTALK_VALIDATION_MSG.SCHEDULED_PAST;
  }

  return null;
};

/**
 * 폼 쿠폰 항목 → DTO 항목: 표시/검증 파생 필드(name·periodType·날짜) 제거.
 * 만료 유형은 UI에서 수량을 받지 않으므로 백엔드 @Min(1)을 만족하는 고정값으로 정규화 (워커는 만료에서 수량 미사용)
 */
const normalizeCouponList = (values: AlimtalkSendFormValues, requirements: FieldRequirements) =>
  values.couponList.map((coupon) => ({
    couponId: coupon.couponId,
    issuedCount: requirements.isExpireReorderable ? ALIMTALK_EXPIRE_ISSUED_COUNT : coupon.issuedCount,
  }));

/** 모드별로 필요한 필드만 포함해 DTO 구성 (CSV 발송이 쿠폰 파라미터를 싣지 않게) */
export const buildCreateAlimtalkSendDto = (
  values: AlimtalkSendFormValues,
  requirements: FieldRequirements,
): CreateAlimtalkSendDto => ({
  templateId: values.templateId as number,
  // EXPIRE는 csvS3Key를 보내면 400 — 필드 자체를 생략 (유형 전환으로 폼에 남은 stale 값도 차단)
  ...(requirements.needsCsvUpload ? { csvS3Key: values.csvS3Key as string } : {}),
  memo: values.memo,
  ...(values.scheduledAt ? { scheduledAt: values.scheduledAt } : {}),
  ...(requirements.needsCouponList ? { couponList: normalizeCouponList(values, requirements) } : {}),
  ...(requirements.needsAlimtalkCouponId ? { alimtalkCouponId: values.alimtalkCouponId as number } : {}),
  ...(requirements.needsEventId ? { eventId: values.eventId as number } : {}),
  ...(requirements.needsValidityDates
    ? { couponStartDate: values.couponStartDate as string, couponEndDate: values.couponEndDate as string }
    : {}),
});

/**
 * 테스트 발송 DTO — 백엔드 TestAlimtalkSendDto extends CreateAlimtalkSendDto.
 * 등록과 동일한 전체 payload + testPhone(10~11자리 숫자만 — 하이픈 등 비숫자 제거).
 */
export const buildTestAlimtalkSendDto = (
  values: AlimtalkSendFormValues,
  requirements: FieldRequirements,
  testPhone: string,
): TestAlimtalkSendDto => ({
  ...buildCreateAlimtalkSendDto(values, requirements),
  testPhone: normalizeTestPhone(testPhone),
});
